import classNames from 'clsx';
import React, { memo, ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { areEqual, ListChildComponentProps } from 'react-window';
import { IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { getFeatureHideCreateUploadInRoFolders } from 'reactApp/modules/features/features.selectors';
import { isReadOnly } from 'reactApp/modules/home/home.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorageCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { AdvBanner } from 'reactApp/ui/Mobile/Datalist/AdvBanner/AdvBanner';

import styles from './VirtualList.css';

export const VirtualListGridRow = memo(function VirtualListGridRow(props: ListChildComponentProps): ReactElement | null {
    const { index, style, data } = props;
    const { list, width, margin, height, itemActiveIndex, indexes, dividerSize } = data;

    const startIndex = indexes[index].start;
    const lastIndex = Math.min(indexes[index].end, list.length);
    const handleRef = index === itemActiveIndex ? data.handleRef : null;
    const isDivider = list[startIndex].divider;

    const currentStorage = useSelector(getCurrentStorage) as EStorageType;
    const folder = useSelector((state: RootState) => getStorageCurrentFolder(state, currentStorage as EStorageType));
    const isReadOnlyFolder = useSelector((state: RootState) => isReadOnly(state, folder?.id));
    const hideCreateUploadInRoFolders = useSelector(getFeatureHideCreateUploadInRoFolders);

    const content = useMemo(() => {
        const output: ReactElement[] = [];

        for (let i = startIndex; i <= lastIndex; i++) {
            const skipRenderItem = list[i]?.divider || (IS_BIZ_USER && i === 0 && isReadOnlyFolder && hideCreateUploadInRoFolders);

            if (skipRenderItem) {
                continue;
            }

            const key = String(typeof list[i] === 'string' ? list[i] : list[i].id);
            const itemStyle = { width: `${width}px`, marginRight: i === lastIndex ? 0 : `${margin}px`, height: `${height}px` };

            output.push(
                <div key={key + i} style={itemStyle}>
                    <div className={styles.colItem}>{data.renderItem(list[i], { index: i })}</div>
                </div>
            );
        }

        return output;
    }, [startIndex, lastIndex, height, list, data, margin, width, isReadOnlyFolder]);

    if (isDivider) {
        return (
            <div
                key={index}
                className={classNames({
                    [styles.dividerContainer]: true,
                    [styles.dividerGrid]: true,
                    [styles.dividerAdv]: list[startIndex].showAdv,
                })}
                style={style}
            >
                <div
                    className={classNames({
                        [styles.divider]: true,
                        [styles[`divider_${dividerSize}`]]: !!dividerSize,
                    })}
                >
                    {list[startIndex].text}
                    {list[startIndex].showAdv && <AdvBanner />}
                </div>
            </div>
        );
    }

    return (
        <div style={style} className={styles.gridRow} ref={handleRef}>
            {content}
        </div>
    );
}, areEqual);
